/**
 * Role-Based Access Control Utilities
 * Enhanced with dynamic ACL support
 * 
 * This module provides both legacy role-based checks (for backward compatibility)
 * and dynamic permission checks using the ACL system
 */

// ==================== Role Constants ====================
export const ROLES = {
  SUPER_ADMIN: 'principal',
  ADMIN: 'partner',
  CLIENT: 'franchisee',
  MODERATOR: 'telco_partner',
  VIEWER: 'user'
};

// ==================== Role Hierarchy ====================
// Lower number = higher privilege.  principal > partner > franchisee > telco_partner > user
const ROLE_LEVELS = {
  principal: 0,
  partner: 1,
  franchisee: 2,
  telco_partner: 3,
  user: 4,
  accounting: 5,
  // Legacy names for backward compatibility (pre-migration data)
  super_admin: 0,
  admin: 1,
  client: 2,
  moderator: 3,
  viewer: 4,
};

export const getRoleLevel = (role) => ROLE_LEVELS[role] ?? Infinity;

// Permissions that should never be assignable by non-Principal accounts.
const NON_PRINCIPAL_BLOCKED_PERMISSIONS = new Set([
  'create_partners',
  'view_partners',
  'edit_partners',
  'delete_partners',
  'view_all_clients',
  'update_all_clients',
  'delete_all_clients',
  'create_permissions',
  'edit_permissions',
  'delete_permissions'
]);

/**
 * Filter permissions visible/assignable for the current actor.
 * Partners, franchisees, and users should not see blocked system/global permissions.
 */
export const getAssignablePermissionsForActor = (permissions = [], actorRole = null, actorPartnerType = null) => {
  const list = Array.isArray(permissions) ? permissions : [];
  const isPrincipal = actorRole === ROLES.SUPER_ADMIN;
  const isPartnerAccount = actorRole === ROLES.ADMIN && ['store_partner', 'telco_partner', 'individual_partner'].includes(actorPartnerType);

  if (isPrincipal || isPartnerAccount) return list;
  return list.filter((perm) => !NON_PRINCIPAL_BLOCKED_PERMISSIONS.has(perm?.name));
};

/** Roles strictly below the given role in the hierarchy */
export const getCreatableRoles = (role) => {
  const myLevel = getRoleLevel(role);
  return Object.entries(ROLE_LEVELS)
    .filter(([, level]) => level > myLevel)
    .map(([name]) => name);
};

/**
 * Partner-type-aware version of getCreatableRoles.
 *
 *  telco_partner  → can create franchisees (client) and below
 *  store_partner  → cannot create any sub-accounts
 *  individual_partner → cannot create any sub-accounts
 *
 * For non-admin roles (super_admin, client, etc.) falls back to the standard hierarchy.
 */
export const getCreatableRolesByPartnerType = (role, partnerType) => {
  const r = normalizeRole(role);
  if (r !== 'partner') return getCreatableRoles(r);
  if (partnerType === 'telco_partner') return ['franchisee', 'telco_partner', 'user'];
  // store_partner / individual_partner / null → no sub-accounts
  return [];
};

/** Whether actorRole outranks targetRole */
export const outranks = (actorRole, targetRole) =>
  getRoleLevel(actorRole) < getRoleLevel(targetRole);

export const TABS = {
  PROFILE: 'profile',
  SPONSORS: 'sponsors',
  PROJECTS: 'projects',
  VOUCHERS: 'vouchers',
  ACTIVE_DEVICES: 'active-devices',
  ACL_MANAGEMENT: 'acl-management',
  CLIENTS: 'clients',
  SETTINGS: 'settings',
  SALES: 'sales',
  TRANSACTIONS: 'transactions',
  MAINTENANCE: 'maintenance',
  SOA_CONFIG: 'soa-config',
  BIN: 'bin'
};

// ==================== Permission to Tab Mapping ====================
// Maps permissions to their corresponding tabs
const PERMISSION_TAB_MAP = {
  'view_sponsors': TABS.SPONSORS,
  'add_sponsors': TABS.SPONSORS,
  'edit_sponsors': TABS.SPONSORS,
  'delete_sponsors': TABS.SPONSORS,
  
  'view_projects': TABS.PROJECTS,
  'add_projects': TABS.PROJECTS,
  'edit_projects': TABS.PROJECTS,
  'delete_projects': TABS.PROJECTS,
  'toggle_projects': TABS.PROJECTS,
  
  'view_vouchers': TABS.VOUCHERS,
  'generate_vouchers': TABS.VOUCHERS,
  'export_reports': TABS.VOUCHERS,
  'view_sales': TABS.SALES,
  'view_transactions': TABS.TRANSACTIONS,
  
  'view_active_devices': TABS.ACTIVE_DEVICES,
  'disconnect_devices': TABS.ACTIVE_DEVICES,
  
  // All ACL-related permissions now map to the unified ACL management tab
  'view_admins': TABS.ACL_MANAGEMENT,
  'add_admins': TABS.ACL_MANAGEMENT,
  'edit_admins': TABS.ACL_MANAGEMENT,
  'delete_admins': TABS.ACL_MANAGEMENT,
  'view_roles': TABS.ACL_MANAGEMENT,
  'create_roles': TABS.ACL_MANAGEMENT,
  'edit_roles': TABS.ACL_MANAGEMENT,
  'delete_roles': TABS.ACL_MANAGEMENT,
  'assign_roles': TABS.ACL_MANAGEMENT,
  'view_permissions': TABS.ACL_MANAGEMENT,
  'create_permissions': TABS.ACL_MANAGEMENT,
  'edit_permissions': TABS.ACL_MANAGEMENT,
  'delete_permissions': TABS.ACL_MANAGEMENT,
  'assign_permissions': TABS.ACL_MANAGEMENT,
  'view_user_permissions': TABS.ACL_MANAGEMENT,

  // Client management permissions
  'create_clients': TABS.CLIENTS,
  'view_own_clients': TABS.CLIENTS,
  'view_all_clients': TABS.CLIENTS,
  'update_own_clients': TABS.CLIENTS,
  'update_all_clients': TABS.CLIENTS,
  'delete_own_clients': TABS.CLIENTS,
  'delete_all_clients': TABS.CLIENTS,

  'view_settings': TABS.SETTINGS,
  'edit_settings': TABS.SETTINGS,

  // Maintenance settings
  'view_maintenance': TABS.MAINTENANCE,
  'edit_maintenance': TABS.MAINTENANCE,

  // SOA Configuration
  'view_soa_config': TABS.SOA_CONFIG,
  'edit_soa_config': TABS.SOA_CONFIG,

  'view_self_profile': TABS.PROFILE,
  'update_self_profile': TABS.PROFILE,

  // Partner management permissions — rendered inside the ACL management tab
  'create_partners': TABS.ACL_MANAGEMENT,
  'view_partners': TABS.ACL_MANAGEMENT,
  'edit_partners': TABS.ACL_MANAGEMENT,
  'delete_partners': TABS.ACL_MANAGEMENT
};

// ==================== Legacy Action to Permission Mapping ====================
// Maps legacy action names to new permission names
const LEGACY_ACTION_MAP = {
  'canAddSponsor': 'add_sponsors',
  'canEditSponsor': 'edit_sponsors',
  'canDeleteSponsor': 'delete_sponsors',
  
  'canAddProject': 'add_projects',
  'canEditProject': 'edit_projects',
  'canDeleteProject': 'delete_projects',
  'canToggleProject': 'toggle_projects',
  
  'canGenerateVoucher': 'generate_vouchers',
  'canViewVouchers': 'view_vouchers',
  'canExportReport': 'export_reports',
  
  'canViewActiveDevices': 'view_active_devices',
  'canDisconnectDevice': 'disconnect_devices',
  
  'canManageAdmins': 'view_admins',

  'canEditSettings': 'edit_settings',
  'canViewSettings': 'view_settings',

  'canCreateClient': 'create_clients',
  'canViewOwnClients': 'view_own_clients',
  'canViewAllClients': 'view_all_clients',
  'canUpdateOwnClients': 'update_own_clients',
  'canUpdateAllClients': 'update_all_clients',
  'canDeleteOwnClients': 'delete_own_clients',
  'canDeleteAllClients': 'delete_all_clients',
  'canViewSelfProfile': 'view_self_profile',
  'canUpdateSelfProfile': 'update_self_profile',
  'canViewSales': 'view_sales',
  'canViewTransactions': 'view_transactions',
};

// ==================== User Permissions Store ====================
// This will be populated when user logs in
let userPermissions = [];
let userRoles = [];

/**
 * Set current user's permissions (called after login)
 */
export const setUserPermissions = (permissions, roles = []) => {
  userPermissions = Array.isArray(permissions) ? permissions : [];
  userRoles = Array.isArray(roles) ? roles : [];
};

/**
 * Get current user's permissions
 */
export const getUserPermissions = () => {
  return userPermissions;
};

/**
 * Get current user's roles
 */
export const getUserRoles = () => {
  return userRoles;
};

/**
 * Clear user permissions (on logout)
 */
export const clearUserPermissions = () => {
  userPermissions = [];
  userRoles = [];
};

// ==================== Dynamic Permission Checking ====================

/**
 * Check if user has a specific permission (by permission name)
 */
export const hasPermissionByName = (permissionName) => {
  return userPermissions.includes(permissionName);
};

/**
 * Check if user has any of the specified permissions
 */
export const hasAnyPermission = (permissionNames) => {
  if (!Array.isArray(permissionNames)) return false;
  return permissionNames.some(name => userPermissions.includes(name));
};

/**
 * Check if user has all of the specified permissions
 */
export const hasAllPermissions = (permissionNames) => {
  if (!Array.isArray(permissionNames)) return false;
  return permissionNames.every(name => userPermissions.includes(name));
};

/**
 * Get accessible tabs based on user's permissions
 * When user has ACL permissions, ONLY those permissions determine visible tabs.
 * Legacy role is used only when user has no ACL permissions (e.g. pre-ACL users).
 * Profile tab is always first and visible to all logged-in users.
 */
export const getAccessibleTabs = (legacyRole = null) => {
  let tabs = [];

  if (userPermissions.length > 0) {
    const accessibleTabs = new Set();
    userPermissions.forEach(permission => {
      const tab = PERMISSION_TAB_MAP[permission];
      if (tab) accessibleTabs.add(tab);
    });
    tabs = Array.from(accessibleTabs);
  } else if (legacyRole) {
    tabs = getLegacyAccessibleTabs(legacyRole);
  }

  // Profile is always first and visible to all logged-in users
  if (!tabs.includes(TABS.PROFILE)) {
    tabs.unshift(TABS.PROFILE);
  } else {
    tabs = [TABS.PROFILE, ...tabs.filter(t => t !== TABS.PROFILE)];
  }
  // Bin is always available to all logged-in users
  if (!tabs.includes(TABS.BIN)) {
    tabs.push(TABS.BIN);
  }
  return tabs;
};

/**
 * Check if user can view a specific tab
 */
export const canViewTab = (tab, legacyRole = null) => {
  const accessibleTabs = getAccessibleTabs(legacyRole);
  return accessibleTabs.includes(tab);
};

/**
 * Check if user has permission for a legacy action name
 * This provides backward compatibility with existing code
 */
export const hasPermission = (legacyRole, permissionName) => {
  // Check if it's a legacy action name
  const mappedPermission = LEGACY_ACTION_MAP[permissionName];
  
  if (mappedPermission) {
    // Use dynamic permission if available
    if (userPermissions.length > 0) {
      return hasPermissionByName(mappedPermission);
    }
    
    // Fallback to legacy check
    return checkLegacyPermission(legacyRole, permissionName);
  }
  
  // If not a legacy action, try direct permission check
  if (userPermissions.length > 0) {
    return hasPermissionByName(permissionName);
  }
  
  return false;
};

// ==================== Legacy Compatibility Functions ====================

/**
 * Legacy tab access mapping (for backward compatibility)
 */
const PRINCIPAL_TABS = [
  TABS.PROFILE,
  TABS.SALES,
  TABS.TRANSACTIONS,
  TABS.SPONSORS,
  TABS.PROJECTS,
  TABS.VOUCHERS,
  TABS.ACTIVE_DEVICES,
  TABS.ACL_MANAGEMENT,
  TABS.CLIENTS,
  TABS.SETTINGS,
  TABS.BIN,
];
const PARTNER_TABS = [
  TABS.PROFILE,
  TABS.SALES,
  TABS.TRANSACTIONS,
  TABS.PROJECTS,
  TABS.VOUCHERS,
  TABS.ACTIVE_DEVICES,
  TABS.ACL_MANAGEMENT,
  TABS.CLIENTS,
  TABS.BIN,
];
const FRANCHISEE_TABS = [
  TABS.PROFILE,
  TABS.SALES,
  TABS.TRANSACTIONS,
  TABS.PROJECTS,
  TABS.VOUCHERS,
  TABS.ACTIVE_DEVICES,
  TABS.CLIENTS,
  TABS.BIN,
];
const TELCO_PARTNER_TABS = [
  TABS.PROFILE,
  TABS.SALES,
  TABS.TRANSACTIONS,
  TABS.SPONSORS,
  TABS.PROJECTS,
  TABS.VOUCHERS,
  TABS.ACTIVE_DEVICES,
  TABS.ACL_MANAGEMENT,
  TABS.CLIENTS,
  TABS.SETTINGS,
  TABS.BIN,
];
const MODERATOR_TABS = [
  TABS.PROFILE,
  TABS.SALES,
  TABS.TRANSACTIONS,
  TABS.SPONSORS,
  TABS.PROJECTS,
  TABS.VOUCHERS,
  TABS.ACTIVE_DEVICES,
  TABS.ACL_MANAGEMENT,
  TABS.CLIENTS,
  TABS.SETTINGS,
  TABS.SOA_CONFIG,
  TABS.BIN,
];
const USER_TABS = [
  TABS.PROFILE,
  TABS.PROJECTS,
  TABS.BIN,
];
const ACCOUNTING_TABS = [
  TABS.PROFILE,
  TABS.TRANSACTIONS,
  TABS.SALES,
  TABS.SOA_CONFIG,
  TABS.BIN,
];

const LEGACY_TAB_ACCESS = {
  // New role names
  [ROLES.SUPER_ADMIN]: PRINCIPAL_TABS,
  [ROLES.ADMIN]: PARTNER_TABS,
  [ROLES.CLIENT]: FRANCHISEE_TABS,
  [ROLES.MODERATOR]: TELCO_PARTNER_TABS,
  [ROLES.VIEWER]: USER_TABS,
  moderator: MODERATOR_TABS,
  accounting: ACCOUNTING_TABS,
  // Legacy role names (pre-migration)
  super_admin: PRINCIPAL_TABS,
  admin: PARTNER_TABS,
  client: FRANCHISEE_TABS,
  viewer: USER_TABS,
};

/**
 * Get accessible tabs for legacy role
 */
const getLegacyAccessibleTabs = (role) => {
  return LEGACY_TAB_ACCESS[role] || [];
};

/**
 * Legacy permission checking (for backward compatibility)
 */
// Normalize legacy role names to new names for permission checks
const normalizeRole = (role) => {
  const map = { super_admin: 'principal', admin: 'partner', client: 'franchisee', moderator: 'telco_partner', viewer: 'user' };
  return map[role] || role;
};

const checkLegacyPermission = (role, permissionName) => {
  const r = normalizeRole(role);
  const LEGACY_PERMISSIONS = {
    canAddSponsor: (r) => [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.CLIENT, ROLES.MODERATOR].includes(r),
    canEditSponsor: (r) => [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.CLIENT, ROLES.MODERATOR].includes(r),
    canDeleteSponsor: (r) => [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.CLIENT, ROLES.MODERATOR].includes(r),

    canAddProject: (r) => [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.CLIENT, ROLES.MODERATOR].includes(r),
    canEditProject: (r) => [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.CLIENT, ROLES.MODERATOR].includes(r),
    canDeleteProject: (r) => [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.CLIENT, ROLES.MODERATOR].includes(r),
    canToggleProject: (r) => [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.CLIENT, ROLES.MODERATOR].includes(r),

    canGenerateVoucher: (r) => [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.CLIENT].includes(r),
    canViewVouchers: (r) => [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.CLIENT, ROLES.MODERATOR].includes(r),
    canExportReport: (r) => [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.CLIENT, ROLES.MODERATOR].includes(r),

    canViewActiveDevices: (r) => [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.CLIENT].includes(r),
    canDisconnectDevice: (r) => [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.CLIENT].includes(r),

    canManageAdmins: (r) => [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(r),

    canViewSettings: (r) => [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(r),
    canEditSettings: (r) => [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(r),

    canCreateClient: (r) => [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.CLIENT].includes(r),
    canViewOwnClients: (r) => [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.CLIENT].includes(r),
    canViewAllClients: (r) => [ROLES.SUPER_ADMIN].includes(r),
    canUpdateOwnClients: (r) => [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.CLIENT].includes(r),
    canUpdateAllClients: (r) => [ROLES.SUPER_ADMIN].includes(r),
    canDeleteOwnClients: (r) => [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.CLIENT].includes(r),
    canDeleteAllClients: (r) => [ROLES.SUPER_ADMIN].includes(r),
    canViewSelfProfile: (r) => true,
    canUpdateSelfProfile: (r) => true,
    canViewSales: (r) => [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.CLIENT].includes(r),
    canViewTransactions: (r) => [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.CLIENT].includes(r),
  };
  
  const permissionFn = LEGACY_PERMISSIONS[permissionName];
  if (typeof permissionFn === 'function') {
    return permissionFn(r);
  }
  return false;
};

/**
 * Check if moderator requires password for report export
 */
export const requiresPasswordForReport = (legacyRole = null) => {
  // Check if user has the permission to export without password
  if (userPermissions.includes('export_reports_without_password')) {
    return false;
  }
  
  // Check if user can export reports at all
  if (userPermissions.includes('export_reports')) {
    return true; // Requires password
  }
  
  // Fallback to legacy check
  const nr = normalizeRole(legacyRole);
  return nr === ROLES.MODERATOR;
};

/**
 * Check if user is read-only
 */
export const isReadOnly = (legacyRole = null) => {
  // If user has any write permissions, not read-only
  const writePermissions = [
    'add_sponsors', 'edit_sponsors', 'delete_sponsors',
    'add_projects', 'edit_projects', 'delete_projects', 'toggle_projects',
    'generate_vouchers',
    'disconnect_devices',
    'add_admins', 'edit_admins', 'delete_admins',
    'create_roles', 'edit_roles', 'delete_roles',
    'create_permissions', 'edit_permissions', 'delete_permissions',
    'assign_roles', 'assign_permissions',
    'edit_settings'
  ];
  
  if (hasAnyPermission(writePermissions)) {
    return false;
  }
  
  // Fallback to legacy check
  const nr = normalizeRole(legacyRole);
  return nr === ROLES.VIEWER;
};

// ==================== Legacy PERMISSIONS Object (Deprecated but maintained for compatibility) ====================
/**
 * @deprecated Use hasPermission() or hasPermissionByName() instead
 * Legacy PERMISSIONS object maintained for backward compatibility
 */
export const PERMISSIONS = {
  // Sponsor permissions
  canAddSponsor: (role) => {
    if (userPermissions.length > 0) {
      return hasPermissionByName('add_sponsors');
    }
    return checkLegacyPermission(role, 'canAddSponsor');
  },
  canEditSponsor: (role) => {
    if (userPermissions.length > 0) {
      return hasPermissionByName('edit_sponsors');
    }
    return checkLegacyPermission(role, 'canEditSponsor');
  },
  canDeleteSponsor: (role) => {
    if (userPermissions.length > 0) {
      return hasPermissionByName('delete_sponsors');
    }
    return checkLegacyPermission(role, 'canDeleteSponsor');
  },
  
  // Project permissions
  canAddProject: (role) => {
    if (userPermissions.length > 0) {
      return hasPermissionByName('add_projects');
    }
    return checkLegacyPermission(role, 'canAddProject');
  },
  canEditProject: (role) => {
    if (userPermissions.length > 0) {
      return hasPermissionByName('edit_projects');
    }
    return checkLegacyPermission(role, 'canEditProject');
  },
  canDeleteProject: (role) => {
    if (userPermissions.length > 0) {
      return hasPermissionByName('delete_projects');
    }
    return checkLegacyPermission(role, 'canDeleteProject');
  },
  canToggleProject: (role) => {
    if (userPermissions.length > 0) {
      return hasPermissionByName('toggle_projects');
    }
    return checkLegacyPermission(role, 'canToggleProject');
  },

  canViewSettings: (role) => {
    if (userPermissions.length > 0) {
      return hasPermissionByName('view_settings');
    }
    return checkLegacyPermission(role, 'canViewSettings');
  },
  canEditSettings: (role) => {
    if (userPermissions.length > 0) {
      return hasPermissionByName('edit_settings');
    }
    return checkLegacyPermission(role, 'canEditSettings');
  }, 
  // Voucher/Subscription permissions
  canGenerateVoucher: (role) => {
    if (userPermissions.length > 0) {
      return hasPermissionByName('generate_vouchers');
    }
    return checkLegacyPermission(role, 'canGenerateVoucher');
  },
  canViewVouchers: (role) => {
    if (userPermissions.length > 0) {
      return hasPermissionByName('view_vouchers');
    }
    return checkLegacyPermission(role, 'canViewVouchers');
  },
  canExportReport: (role) => {
    if (userPermissions.length > 0) {
      return hasPermissionByName('export_reports') || hasPermissionByName('export_reports_without_password');
    }
    return checkLegacyPermission(role, 'canExportReport');
  },
  requiresPasswordForReport: (role) => {
    return requiresPasswordForReport(role);
  },
  
  // Active Devices permissions
  canViewActiveDevices: (role) => {
    if (userPermissions.length > 0) {
      return hasPermissionByName('view_active_devices');
    }
    return checkLegacyPermission(role, 'canViewActiveDevices');
  },
  canDisconnectDevice: (role) => {
    if (userPermissions.length > 0) {
      return hasPermissionByName('disconnect_devices');
    }
    return checkLegacyPermission(role, 'canDisconnectDevice');
  },
  
  // Access Control permissions
  canManageAdmins: (role) => {
    if (userPermissions.length > 0) {
      return hasPermissionByName('view_admins');
    }
    return checkLegacyPermission(role, 'canManageAdmins');
  },
  
  // Client management permissions
  canCreateClient: (role) => {
    if (userPermissions.length > 0) {
      return hasPermissionByName('create_clients');
    }
    return checkLegacyPermission(role, 'canCreateClient');
  },
  canViewClients: (role) => {
    if (userPermissions.length > 0) {
      return hasPermissionByName('view_own_clients') || hasPermissionByName('view_all_clients');
    }
    return checkLegacyPermission(role, 'canViewOwnClients');
  },
  canUpdateClients: (role) => {
    if (userPermissions.length > 0) {
      return hasPermissionByName('update_own_clients') || hasPermissionByName('update_all_clients');
    }
    return checkLegacyPermission(role, 'canUpdateOwnClients');
  },
  canDeleteClients: (role) => {
    if (userPermissions.length > 0) {
      return hasPermissionByName('delete_own_clients') || hasPermissionByName('delete_all_clients');
    }
    return checkLegacyPermission(role, 'canDeleteOwnClients');
  },

  // Transaction permissions
  canViewTransactions: (role) => {
    if (userPermissions.length > 0) {
      return hasPermissionByName('view_transactions');
    }
    return checkLegacyPermission(role, 'canViewTransactions');
  },

  // Sales permissions
  canViewSales: (role) => {
    if (userPermissions.length > 0) {
      return hasPermissionByName('view_sales');
    }
    return checkLegacyPermission(role, 'canViewSales');
  },

  // General permissions
  canViewTab: (role, tab) => {
    return canViewTab(tab, role);
  },
  isReadOnly: (role) => {
    return isReadOnly(role);
  }
};

/**
 * Get user role display name
 */
export const getRoleDisplayName = (role) => {
  const roleNames = {
    [ROLES.SUPER_ADMIN]: 'Principal',
    [ROLES.ADMIN]: 'Partner',
    [ROLES.CLIENT]: 'Franchisee',
    [ROLES.MODERATOR]: 'Telco Partner',
    [ROLES.VIEWER]: 'User',
    moderator: 'Moderator',
    accounting: 'Accounting',
    // Legacy names
    super_admin: 'Principal',
    admin: 'Partner',
    client: 'Franchisee',
    viewer: 'User',
  };
  return roleNames[role] || 'Unknown';
};

/**
 * Get role badge color
 */
export const getRoleBadgeColor = (role) => {
  const colors = {
    [ROLES.SUPER_ADMIN]: '#cf1322',
    [ROLES.ADMIN]: '#1890ff',
    [ROLES.CLIENT]: '#722ed1',
    [ROLES.MODERATOR]: '#fa8c16',
    [ROLES.VIEWER]: '#52c41a',
    moderator: '#597ef7',
    accounting: '#13c2c2',
    // Legacy names
    super_admin: '#cf1322',
    admin: '#1890ff',
    client: '#722ed1',
    viewer: '#52c41a',
  };
  return colors[role] || '#8c8c8c';
};

